// Case studies catalog - real user wins powered by Xynth AI analysis
// Each entry maps to a detailed article page at /case-studies/[slug]

export type TradeStatus = "active" | "exited" | "to-enter";

export interface CaseStudy {
  slug: string;
  isPublished: boolean;
  automationAlertId?: string;
  automationCtaLabel?: string;
  headline: string;
  subtitle: string;
  ticker: string;
  returnPct: string;
  returnDollar: string;
  investmentRequired: string;
  thumbnailUrl: string;
  tags: string[];
  readTime: string;
  prompt: string;
  holdingPeriod: string;
  strategy: string;
  tradeDetail: string;
  tradeStatus: TradeStatus;
  publishDate?: string;
  views: number;
  likes: number;
  author?: {
    name: string;
    avatar: string;
    badge: string;
  };
  sections: {
    setup: string;
    whatXynthFound: string[];
    trade: string;
    result: string;
  };
}

const _RAW_CASE_STUDIES: CaseStudy[] = [

  {
    slug: "market-recap-jun29-jul3-2026",
    isPublished: true,
    headline: "Market Recap 06/29 – 07/03",
    subtitle: "**TLDR;** Meta says it will sell its excess AI compute, the compute-scarcity trade unwinds, memory and neocloud stocks get hit double digits while software rips. Plus a weak jobs report, Bitcoin off the lows, and Trump pumping Micron.",
    ticker: "META",
    returnPct: "Recap",
    returnDollar: "Weekly",
    investmentRequired: "Free",
    thumbnailUrl: "/images/blog/market-recap-jun29-jul3/supply-chain-returns.png",
    tags: ["Market Recap", "AI Selloff", "SaaS Rotation", "Bitcoin"],
    readTime: "8 min",
    holdingPeriod: "Weekly",
    strategy: "Market Recap",
    tradeDetail: "Week of June 29 – July 3, 2026",
    tradeStatus: "exited",
    publishDate: "July 3, 2026",
    views: 0,
    likes: 0,
    author: {
      name: "Xynth Team",
      avatar: "/logo.png",
      badge: "Weekly Recap",
    },
    prompt: `Give me a recap of this week's market. What drove the semiconductor selloff, where did the money rotate, and what are the key levels and catalysts to watch next week?`,
    sections: {
      setup:
        "Correction, or rotation? After the week the AI trade just had, that's the only question that matters. Midweek, Meta broke the news that it will start selling its excess AI compute to outside customers — and the entire AI hardware trade is underwritten by scarcity. The selloff started in the neocloud compute providers and spread down the stack, feeding a rising 'bottleneck fatigue': bears argue the trade is overcrowded, semis are pushing 20% of the S&P 500, and capital is flowing out of the sector as a whole — the great rotation.",
      whatXynthFound: [
        "Meta confirmed it will sell excess AI compute to outside customers, cracking the compute-scarcity assumption underwriting the AI hardware trade.",
        "The damage was layered: chips barely moved (NVDA +0.5%) while everything downstream bled — memory (MU -13.6%, SNDK -16.5%), photonics (COHR -12.1%), neoclouds (IREN -20.1%, CRWV -16.9%, NBIS -13.5%), and AI power names (CEG -10.3%).",
      ],
      trade:
        "This is a weekly market recap, not a trade. Sections cover the Meta excess-compute story, the memory selloff, the SaaS rotation, the Bitcoin rebound, and the Trump/Micron episode.",
      result:
        "Money rotated out of AI hardware into software while a weak June jobs report reset Fed expectations and Bitcoin reclaimed $60K off the 200-week moving average.",
    },
  },

  {
    slug: "czr-merger-100pct",
    isPublished: true,
    headline: "Turning $1k to $3.5 in 5 days by detecting news before it broke out.",
    subtitle: "**TLDR;** I use Xynth to flag unusual options activity, and conduct technical analysis to find stocks before they jump. The latest being $CZR, where we detected it before major news came out and netted 250% returns.",
    ticker: "CZR",
    returnPct: "+250%",
    returnDollar: "+$2,360",
    investmentRequired: "$944",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-czr-merger-100pct/20260628T181009-ea0ced66.png",
    tags: ["Calls", "Options Flow", "Swing Trade", "Merger"],
    readTime: "4 min",
    holdingPeriod: "4 days",
    strategy: "Weekly Calls",
    tradeDetail: "$27 Call · Mar 20 · $1.18 entry",
    tradeStatus: "exited",
    publishDate: "March 23, 2026",
    views: 14200,
    likes: 891,
    author: {
      name: "danvega_trades",
      avatar: "/images/case-studies/czr-merger/profile-pic.png",
      badge: "Top 1% Xynth User",
    },
    prompt: `Hey Xynth, I need you to find me stocks that have:

1. High abnormal options activity
2. Have had a change of more than 5% over the last month

Then for each of the top candidates, I need you to analyze their Technicals and bring me the top 3 candidates.

Then for each of these candidates, analyze their options chains 1 week out from now and then scout to see if there are any opportunities. Rank these opportunities based on risk/reward and gamma levels / likeliness to hit. For the top opportunity, break down the exact entry, target, and take profit levels.`,
    sections: {
      setup:
        "A user wanted to find stocks where institutional money was quietly positioning before a catalyst. They asked Xynth to scan for unusual options flow combined with recent momentum, then drill into technicals and gamma levels for the best entry.",
      whatXynthFound: [
        "Xynth scanned 20 tickers with abnormal options flow and recent momentum. CZR came in third by flow premium at $305K, but was the only one with a BULLISH flow direction, +30.6% momentum, and a reasonable IV rank of 39.3.",
        "Ran a technical scorecard across the top 8 candidates, scoring each on MA alignment, RSI, MACD, ADX trend strength, Supertrend, volume, and Bollinger Bands. CZR scored 92/100, the highest, with a perfect 25/25 on moving average alignment and 10/10 on Bollinger positioning.",
        "Mapped out the $27 Call payoff at expiry to visualize the exact risk/reward. Entry at $1.18 per contract, breakeven at $28.18, with uncapped upside above that level.",
      ],
      trade:
        "CZR $27 Call, Mar 20 expiry. Entry: $1.18/contract. Target: $30 (gamma wall). Stop: $25 (gamma support). Risk/Reward: 1.4:1.",
      result:
        "Turns out, the bullish flow we were picking up on was anticipating a $7 billion buyout. On March 11, Caesars jumped 11.8% after news broke that billionaire Tilman Fertitta was in exclusive talks to acquire the company at $34/share. The $27 Calls we entered at $1.18 ran to $4.13, netting a 250% return in 5 days.",
    },
  },
  {
    slug: "spx-oil-0dte-83pct",
    isPublished: true,
    headline: "Iran-US Oil 0DTE Opening Strategy with 83% Win Rate",
    subtitle: "**TLDR;** Oil futures down premarket = upwards opening move for SPX. Take gains by trading 0DTE options in the first 30 minutes.",
    ticker: "SPX",
    returnPct: "+59%",
    returnDollar: "+$2,810",
    investmentRequired: "$8,174",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-spx-oil-0dte-83pct/20260628T181005-e3925706.png",
    tags: ["0DTE", "Oil Correlation", "SPX", "Opening Range"],
    readTime: "6 min",
    holdingPeriod: "16 min",
    strategy: "0DTE Calls",
    tradeDetail: "SPX $6700 Call · Mar 17 · $40.87 entry",
    tradeStatus: "exited",
    publishDate: "March 16, 2026",
    views: 9800,
    likes: 634,
    author: {
      name: "keshavnair",
      avatar: "/images/case-studies/spx-oil-0dte/avatar.png",
      badge: "Top 1% Xynth User",
    },
    prompt: `Hey Xynth, I want to run my oil-SPX opening range check for today. Here's what I need:

1. Pull up crude oil futures ($CL) and show me the overnight/premarket trend. Is oil trending down heading into the open? Include the % change from yesterday's close.

2. Look at yesterday's closing session options flow on today's SPX 0DTE expiry (last 30 min of flow). Break down the bullish vs bearish dollar volume and trade count. I want to see if there's a clear directional lean.

3. Check SPX gamma positioning. Are we sitting near a positive gamma wall that could pin price, or are we in negative gamma territory where dealers would amplify moves? Show me where SPOT is relative to the key gamma levels.

4. Based on all 3 filters, suggest whether today looks like a good setup for a 0DTE SPX call to capture an opening 30-min rally, or if I should sit this one out. If conditions don't align cleanly, just tell me to skip it.`,
    sections: {
      setup:
        "A trader noticed a repeating pattern: when crude oil futures trend down before market open, SPX tends to rally in the first 30 minutes. With the Iran-US situation, oil became a proxy for geopolitical temperature — lower oil = de-escalation = relief rally. He built a system around it.",
      whatXynthFound: [
        "Oil futures ($CL) had been dropping steadily overnight after news broke that Trump was allowing Iranian tankers through the Strait. Crude was down 2.3% by 9:00 AM — a strong signal.",
        "Yesterday's closing flow on next-day expiry: 94 bullish trades totaling $4.4M vs 57 bearish trades totaling $1.1M. That's a 4:1 bullish ratio in dollar terms.",
        "SPX closed at 5699 in a negative gamma regime. No positive gamma walls nearby — price was free to move. Large negative gamma 1 point above close meant dealers would amplify any upward move.",
        "The SPX $6700 Call opened at $41.15 at 9:30 AM and ran to $56.70 by 9:46 AM — 16 minutes. Exit was taken early because theta decay on 0DTE is brutal.",
      ],
      trade:
        "SPX $6700 Call, Mar 17 0DTE. 2 contracts at $40.87 average. Strategy: capture the opening 30-min rally when all 3 filters align (oil down, flow bullish, gamma clear).",
      result:
        "The contract moved from $40.87 to $43.70, a +59% return on this particular trade. Total P&L: +$2,810. The strategy has been traded 12 times with a 10/12 (83%) win rate. As long as oil remains a proxy for geopolitical sentiment, the correlation holds.",
    },
  },
  {
    slug: "earnings-iv-crush-84pct",
    isPublished: true,
    automationAlertId: "alert-83e89c3f70e3",
    automationCtaLabel: "Open Earnings IV Crush Scanner",
    headline: "How I Use AI to Trade Through Earnings, 84.74% Returns So Far",
    subtitle: "**TLDR;** I use AI to find overpriced options right before earnings, then trade a short straddle setup betting on the IV crush. Averaging ~84.74% annual returns.",
    ticker: "PLD",
    returnPct: "+84.74%",
    returnDollar: "+$24,135",
    investmentRequired: "$10,000",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-earnings-iv-crush-84pct/20260628T181005-346046f5.png",
    tags: ["Earnings", "IV Crush", "Short Straddle", "Screening"],
    readTime: "8 min",
    holdingPeriod: "Overnight",
    strategy: "Short Straddle",
    tradeDetail: "ATM Straddle · Earnings Eve → Morning After",
    tradeStatus: "exited",
    publishDate: "March 22, 2026",
    views: 11400,
    likes: 752,
    author: {
      name: "marcellinhoo",
      avatar: "/images/case-studies/earnings-iv-crush/author.png",
      badge: "Top 5% Xynth User",
    },
    prompt: `Screen for stocks: US markets, primary listing, no OTC. Market cap above $1B. Sort by market cap and take the top 100.

Pull their earnings data from the past 2 years. Then analyze intraday price action around each earnings date to identify stocks with consistent movements.

From that list, filter out which stocks have upcoming earnings, and stock stocks with:
1. Negative term structure slope below -15%. Formula: (IV 40-45 days out - IV nearest expiration) / IV Front × 100%
2. IV/RV ratio above 1.25

For the stocks that pass all filters, show me the ATM short straddle setup on the nearest post-earnings expiry. Include the premium collected, breakeven range, and max risk.`,
    sections: {
      setup:
        "I notice that options get expensive before earnings due to uncertainty. The opportunity: when IV overestimates the actual move, the excess premium evaporates post-earnings. This is called an IV crush. The key is filtering for the RIGHT earnings events.",
      whatXynthFound: [
        "Screening 5,388 stocks matching basic criteria (US market, >$1B cap, primary listing). Pulling 2 years of historical earnings data and analyzing overnight price consistency around each report.",
        "Filtering for negative term structure slope below -15%: finding PEP (-36.2%), WFC (-20.1%), C (-19.2%), ABT (-18.0%), GS (-16.4%), MS (-15.4%), JPM (-15.0%) among the top candidates.",
        "Cross-checking IV/RV ratios: looking for stocks where implied volatility is pricing in 25%+ more movement than recent realized volatility. These are the ones where fear is most overpriced.",
        "Building ATM short straddle setups on the top candidates with nearest post-earnings expiry, showing premium collected, breakeven range, and risk profile.",
      ],
      trade:
        "ATM short straddle on filtered earnings candidates. Selling both the ATM call and ATM put with the same expiration (nearest after earnings). Entry 15 min before close the day before earnings. Exit within 15 min after open the next day.",
      result:
        "Over 2 years of trading this strategy: CAGR of 84.74% vs SPY's 25.62%. Win rate of 65%. Average return per trade ~10%. Max loss on a single trade is 90%. Max drawdown ~25% over a 2-month period. The edge is entirely in the overnight IV crush: get in, get out, no holding.",
    },
  },
  {
    slug: "nvda-iran-short-77pct",
    isPublished: true,
    headline: "Shorting NVDA Because of the Iran War. Made $2,955 in 30 Days. 77% Win Rate.",
    subtitle: "**TLDR;** Iran war made NVDA choppy. Paper traded a shorting strategy for a month, then went live with $10K. 13 trades, 10 wins, 3 losses, ended at $12,955. The strategy uses dealer positioning data (GEX + Charm).",
    ticker: "NVDA",
    returnPct: "+29.5%",
    returnDollar: "+$2,955",
    investmentRequired: "$10,000",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-nvda-iran-short-77pct/20260628T181012-4adc72d6.png",
    tags: ["Short", "GEX", "Dealer Positioning", "NVDA"],
    readTime: "8 min",
    holdingPeriod: "2 days avg",
    strategy: "Short Equity",
    tradeDetail: "NVDA Short · Feb-Mar · 13 trades",
    tradeStatus: "exited",
    publishDate: "March 19, 2026",
    views: 8300,
    likes: 547,
    author: {
      name: "erickooo0",
      avatar: "/images/case-studies/nvda-iran-short/avatar.png",
      badge: "Top 5% Xynth User",
    },
    prompt: `What is NVDA's current Net GEX and Charm? Is Net GEX above $1.1M and Charm above -$274M?

If both conditions are met, move to this:

Deep dive NVDA news, X/Twitter sentiment, and upcoming catalysts for the next 3 days. Any earnings, Fed speakers, CPI prints, tariff announcements, or Iran developments that could break the range?

Based on today's GEX/Charm signal and the catalyst landscape, give me the trade: short entry, stop loss, and 2-day price target.`,
    sections: {
      setup:
        "Ever since Trump started threatening Iran back in January, NVDA has been stuck between $175 and $195. Buying dips kept failing. The trader realized the choppiness wasn't random — something was holding the stock in place. He paper traded for a month to figure it out, then went live.",
      whatXynthFound: [
        "NVDA's Net GEX was elevated at $2.8M — well above the $1.1M threshold. When GEX is high and positive, market makers are pinning the stock: it goes up, they sell; it goes down, they buy. A giant invisible magnet pulling it back to the middle.",
        "Charm was stable at -$103M, above the -$274M danger zone. This means the options hedge isn't melting — the pin is real, not dissolving. During the paper month, 70% of losing trades had Charm below -$274M. Xynth's backtest found this was the missing filter.",
        "No major catalysts in the next 3 days. No earnings, no Fed speakers, no CPI. The Iran ceasefire framework was announced but IRGC rejected it — oil still at $89, chip routes still disrupted. Range-bound conditions intact.",
        "The trade setup: short NVDA at $183.22, stop loss at $186.50 (above the GEX pin), 2-day target at $180.40. Risk/reward 1:0.86 but with a 77% hit rate the expected value is strongly positive.",
      ],
      trade:
        "NVDA short at $183.22. Stop: $186.50. Target: $180.40. 2-day hold. Strategy: when GEX is high and Charm is stable, the market maker pin will pull price back to center.",
      result:
        "$10,000 to $12,955 in 30 days. 13 trades, 10 wins, 3 losses. 77% win rate. Profit factor of 9.7x. Max drawdown 2.1%. The 7-trade win streak from trades 2-8 was the turning point. The three losses all had clear explanations: tariff chaos, a scratch trade, and an overcrowded pin.",
    },
  },
  {
    slug: "vol-skew-spreads-250pct",
    isPublished: true,
    headline: "My Method for Making Money Trading Mispriced Options with AI",
    subtitle: "**TLDR;** Find stocks with abnormal volatility skews using AI, then trade Vertical Spreads on them depending on the direction.",
    ticker: "ORCL",
    returnPct: "+250%",
    returnDollar: "avg winner",
    investmentRequired: "varies",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-vol-skew-spreads-250pct/20260628T181006-24fe2ebd.png",
    tags: ["Vertical Spreads", "Vol Skew", "Mispricing", "Systematic"],
    readTime: "7 min",
    holdingPeriod: "2-3 weeks",
    strategy: "Vertical Spreads",
    tradeDetail: "Bull Call Spread · ATM/OTM · Multi-stock",
    tradeStatus: "exited",
    publishDate: "March 25, 2026",
    views: 7600,
    likes: 483,
    author: {
      name: "thetadecay_",
      avatar: "/images/case-studies/vol-skew-spreads/author.png",
      badge: "Top 5% Xynth User",
    },
    prompt: `Run a full volatility skew mispricing scan on the S&P 500. I want to find stocks where OTM options are abnormally overpriced relative to ATM, then build vertical spreads on the best candidates.

Step 1 - Skew Screen:
For every S&P 500 stock, calculate the 25-delta put skew and call skew vs the 30-day historical average. Compute the z-score for each. Flag any stock where the skew z-score is below -2.0 (i.e., skew is 2+ standard deviations steeper than normal). Show me a ranked table of all flagged stocks with their z-scores, current skew, and historical average skew.

Step 2 - IV vs Realized Vol Mismatch:
For the flagged stocks, compare implied volatility vs 20-day realized volatility at both the ATM strike and the 25-delta OTM strike. I want:
• OTM IV significantly HIGHER than realized vol (overpriced)
• ATM IV roughly EQUAL to or LOWER than realized vol (fairly priced)
Show the IV/RV ratio at each strike for every candidate. Drop any stock where the mismatch doesn't confirm.

Step 3 - Momentum Confirmation:
For the remaining candidates, check directional momentum using EMA 20/50 crossover, RSI (14), and MACD (12, 26, 9). Determine:
• Positive momentum + rich call skew = bull call spread
• Negative momentum + rich put skew = bear put spread
Drop any stock where momentum contradicts the skew direction.

Step 4 - Build the Spread:
For all surviving candidates, construct the optimal vertical spread:
• Buy the ATM option (~50 delta, fairly priced leg)
• Sell the OTM option (~15-25 delta, overpriced leg)
• Expiry: 2-4 weeks out
For each spread show: strikes, net debit, max profit, max loss, breakeven, risk-reward ratio, and a payoff diagram. Score each setup out of 100.`,
    sections: {
      setup:
        "A trader gets tired of gambling on cheap calls from Reddit tips. He decides to learn the actual math behind options pricing and discovers that volatility skew creates repeatable mispricings you can exploit with vertical spreads.",
      whatXynthFound: [
        "Scans the S&P 500 and flags 8 stocks with skew z-scores below -2.0, meaning their current skew is 2+ standard deviations steeper than the 30-day average. Statistically rare and likely to revert.",
        "For the top candidates, compares IV vs realized volatility at each strike. OTM options are pricing in 40-60% more movement than the stock actually delivers. ATM options are fairly priced. That gap is the edge.",
        "Momentum confirmation narrows it down: positive momentum + rich call skew points to a bull call spread. Negative momentum + rich put skew points to a bear put spread.",
        "Builds the spread: buy the ATM option (fairly priced, ~50 delta), sell the OTM option (overpriced, ~15 delta). The overpriced leg subsidizes the trade and creates asymmetric payoff.",
      ],
      trade:
        "Bull call spread on the top-scoring candidate. Buy ATM call (~50 delta), sell OTM call (~15 delta), 3-week expiry. Net debit is reduced because the OTM leg is overpriced. Score: 87/100.",
      result:
        "Win rate sits at ~38% but average winner returns ~250% while average loser costs ~60%. Winning 4 out of 10 trades at 3-4x return covers the 6 losses easily. The strategy is asymmetric by design, not every trade wins but the math works over time.",
    },
  },
  {
    slug: "how-to-day-trade-with-ai",
    isPublished: true,
    automationAlertId: "alert-ed6146b1d3f0",
    automationCtaLabel: "Open Beginner Swing Trades",
    headline: "How to Day Trade with AI",
    subtitle: "**TLDR;** A beginner's guide to the exact workflow behind most of our case studies. Screen the market, narrow your candidates, analyze the best one, and build a trade plan — in 15 minutes.",
    ticker: "AMD",
    returnPct: "Guide",
    returnDollar: "Free",
    investmentRequired: "Any",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-how-to-day-trade-with-ai/20260628T181012-af16b3eb.png",
    tags: ["Beginner", "Stocks", "Step-by-Step"],
    readTime: "5 min",
    holdingPeriod: "Intraday",
    strategy: "Day Trade / Swing",
    tradeDetail: "Beginner's Guide · Full Workflow",
    tradeStatus: "exited",
    publishDate: "March 31, 2026",
    views: 18700,
    likes: 1240,
    author: {
      name: "Xynth Team",
      avatar: "/logo.png",
      badge: "Official Guide",
    },
    prompt: `Find me stocks that are good for day trading. I want:
- Medium volatility (ATR between 4-5%)
- Good daily volume (over 500K shares)
- Early trend strength (price within 0-10% of 20-day SMA)

Show me the top 20 results.

For the top 20, pull their 1-month price charts and run technical analysis on each one. Score them on moving average alignment, RSI, MACD, ADX, volume, and Bollinger Bands. Show me the top 5 ranked by total score.

For the #1 candidate, run a deep technical analysis and check for recent news, upcoming earnings, or analyst activity.

Then build me a trade setup with entry price, stop loss, target, expected duration, position size for a $1,000 account, dollar P&L, and risk-reward ratio. Visualize it on the price chart with entry, stop, and target levels marked.`,
    sections: {
      setup:
        "AI has completely changed the trading landscape. What used to require Bloomberg terminals and years of screen time is now accessible to anyone. This guide walks through the exact workflow — from screening the entire market to building a trade plan with defined risk.",
      whatXynthFound: [
        "Screened for medium-volatility stocks with good volume and early trend signals. Filtered down to 5 candidates showing the right characteristics for swing trading.",
        "Pulled 1-month price charts for all 5 and ran technical analysis across each. Scored them on MA alignment, RSI momentum, MACD direction, ADX trend strength, volume confirmation, and Bollinger Band positioning.",
        "The top candidate scored 92/100 on the technical scorecard. Perfect moving average alignment, RSI in the bullish zone but not overbought, and volume confirming the trend.",
        "Generated 3 distinct trade setups with different risk profiles. The best one had a 2.1:1 risk-reward ratio with clear technical support for the stop loss level.",
      ],
      trade:
        "For the top candidate, Xynth builds actual trade setups — entry, stop loss, take profit, position sizing, risk/reward ratio. Everything calculated and visualized on the chart before you enter.",
      result:
        "The AI doesn't replace your judgment — it accelerates the research that would take hours into minutes. Start wide, narrow progressively, end with a single actionable trade. That's the framework.",
    },
  },
  {
    slug: "ai-options-trading-guide",
    isPublished: true,
    automationAlertId: "alert-9ef6f0026ff7",
    automationCtaLabel: "Open Cheap Options Scanner",
    headline: "How to Trade Options with AI",
    subtitle: "**TLDR;** A beginner's guide to finding and building options trades with AI. Screen for cheap options on trending stocks, confirm with institutional flow, deep dive the chains, and build best-fit trades with P&L diagrams.",
    ticker: "PLUG",
    returnPct: "Guide",
    returnDollar: "Free",
    investmentRequired: "$1,000+",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-ai-options-trading-guide/20260628T181010-165463ed.png",
    tags: ["Options", "Flow Analysis", "Step-by-Step"],
    readTime: "8 min",
    holdingPeriod: "Days–Weeks",
    strategy: "Options Trading",
    tradeDetail: "Beginner's Guide · Options · Full Workflow",
    tradeStatus: "exited",
    publishDate: "March 30, 2026",
    views: 15300,
    likes: 1080,
    author: {
      name: "Xynth Team",
      avatar: "/logo.png",
      badge: "Official Guide",
    },
    prompt: `Find me stocks where the price is already going up (positive 1-month momentum) but options are still cheap (IV rank under 30). Filter for market cap over $1B and options volume over 1,000 contracts/day. Rank the top 10 by cheapest options + strongest momentum. Table and scatter chart.

For those top 10, check institutional options flow — who's placing $50K+ bets, how many are calls vs puts, how many are sweeps. Narrow to the top 3 where big money agrees with the stock's direction. Table and bar chart.

For those 3 finalists, pull the actual options chains — calls, nearest monthly expiry, strikes within 10% of current price — and show me open interest by expiration. I want to see where the liquidity sits and which strikes are tradeable.

For each finalist, design the best-fitting trade and show me a P&L diagram. Pick the right structure — short put for income, bull call spread for defined risk, long call for conviction. Show max profit, max loss, breakeven, and capital required on each chart.`,
    sections: {
      setup:
        "Most people jump into options trading by picking a stock they like and buying a call or put. This guide shows a more systematic approach: use AI to screen hundreds of stocks, narrow down with technicals and news, then analyze the options chain to find the best setup.",
      whatXynthFound: [
        "Screens 500+ stocks using the built-in Stock Screener with filters for market cap, IV rank, momentum, and liquidity. Returns the top 10 candidates ranked by options trading potential.",
        "Pulls RSI, SMA, and volume data for all 10 candidates and plots technical charts. Uses the data to narrow down to the top 2-3 stocks with the cleanest setups.",
        "Analyzes recent news and catalysts for the top picks. Checks for earnings dates, FDA approvals, macro events, or anything that could move the stock in the near term.",
        "Dives into the options chain for the #1 pick. Analyzes OI, volume, put/call ratio, and IV by strike to identify 3-4 potential trade setups with full P&L diagrams.",
      ],
      trade:
        "The final output is 3-4 specific trade setups on the best candidate, each with strike, expiration, entry price, target, stop loss, max profit, max loss, and a P&L diagram.",
      result:
        "This isn't about one trade result — it's a repeatable workflow. Run it weekly to find fresh setups. The AI handles the data processing and screening, you make the final call on which trade to take.",
    },
  },
  {
    slug: "whale-watching-flow-strategy",
    isPublished: true,
    automationAlertId: "alert-05481a1e8c25",
    automationCtaLabel: "Open Whale Watching Scanner",
    headline: "How I Use AI to Track Whale Trades and Make Consistent Money",
    subtitle: "**TLDR;** I automated a \"Whale Watching\" strategy using AI. It scans institutional flow, filters out hedges, checks IV & sentiment, and calculates Risk/Reward. Fresh trade ideas every 4 hours. 56% win rate, +85% avg winner.",
    ticker: "META",
    returnPct: "+85%",
    returnDollar: "avg winner",
    investmentRequired: "varies",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-whale-watching-flow-strategy/20260628T181009-9fff86ad.png",
    tags: ["Options", "Automated", "Swing Trade", "Flow Analysis"],
    readTime: "8 min",
    holdingPeriod: "Days to weeks",
    strategy: "Flow-Based Swing",
    tradeDetail: "Multi-stock · Calls · 6-Step Pipeline",
    tradeStatus: "exited",
    publishDate: "March 30, 2026",
    views: 10100,
    likes: 672,
    author: {
      name: "jonahwhalen",
      avatar: "/images/case-studies/whale-watching/author.png",
      badge: "Verified Trader",
    },
    prompt: `Step 1: Scan the entire market for unusual institutional options activity. Filter for premiums above $50K and expiry within 90 days. Cluster by ticker and show me the top 20 by total premium, broken down by calls vs puts.

Step 2: For each ticker from Step 1, pull detailed flow data and the 20-day SMA. Reject any ticker where the flow direction contradicts the price trend (bearish flow + uptrend = hedge, bullish flow + downtrend = hedge). Keep only where flow matches trend.

Step 3: For the remaining tickers, check IV Rank. Reject anything above the 70th percentile — I don't want to pay inflated premiums.

Step 4: For what's left, grab recent news and social sentiment. Flag and reject any ticker with a binary event (earnings, FDA, lawsuit) in the next 7 days. Score sentiment from -1 to 1 and reject if sentiment contradicts the flow direction.

Step 5: For the surviving trades, adjust the strikes: push expiry out by 14 days for breathing room, and move strikes to within 2% of ATM.

Step 6: Run Black-Scholes on each adjusted trade. Calculate max loss, max profit, probability of profit, and breakeven. Rank by: Score = (Risk/Reward) + (Sentiment Score) - (IV Cost). Show me the final ranked list.`,
    sections: {
      setup:
        "Most people lose money following \"Whales\" because they don't understand hedging. If a fund owns $100M of Apple stock and buys $1M in Puts, that's insurance, not a directional bet. This strategy separates the insurance from the real conviction bets using a 6-step filtering pipeline that runs every 4 hours.",
      whatXynthFound: [
        "Step 1 — Market-wide scan found Tesla, Meta, and Nvidia with large clustered call activity and minimal puts, signaling positive directional bets. QQQ and SPY were heavy on puts — classic portfolio insurance, not crash bets.",
        "Step 2 — Filtered for trend alignment. Tickers where bullish flow matched an uptrend (price above 20-day SMA) were kept. Bearish flow in uptrends was rejected as hedging. Meta and ORCL passed with bullish flow + confirmed uptrend.",
        "Step 3 — IV filter eliminated overpriced setups. Meta's IV Rank was at the 46th percentile — well below the 70th percentile cutoff — meaning premiums were still reasonably priced before the crowd piled in.",
        "Step 4 — AI sentiment check on news and social media. No binary events (earnings/FDA) within 7 days for Meta. Sentiment score positive, aligning with the bullish flow. No contradictions flagged.",
        "Step 5 — Strike adjustment for retail risk management. Meta's original whale strike was Dec 5 expiry, padded to Dec 19 (+14 days). Strike stayed the same since it was already within 2% of ATM.",
        "Step 6 — Black-Scholes scoring ranked Meta Dec 19 $675 Call as the top trade. High R/R ratio, positive sentiment, reasonable IV cost.",
      ],
      trade:
        "META $675 Call, Dec 19 expiry. Adjusted from the original whale flow (Dec 5 expiry) with 14 extra days of breathing room. Score combined Risk/Reward strength, sentiment alignment, and IV cost.",
      result:
        "2 days later, Meta announced a 30% cut in metaverse budget, shifting resources to AI. Stock jumped 3%, contract up 100% in 3 days. Overall strategy stats: 56% win rate, +85% average winner, -30% average loser. The wins are modest but consistent — the 6 layers of risk filtering are the edge.",
    },
  },
  {
    slug: "insider-cluster-small-cap-strategy",
    isPublished: true,
    automationAlertId: "alert-c6f8ce687bd3",
    automationCtaLabel: "Open Insider Cluster Scanner",
    headline: "My strategy for using AI to track insider trading on small cap stocks",
    subtitle: "**TLDR;** I use Xynth to track small cap stocks with clustered insider buyers, non-routine purchases, and large positional entries on common stock by company executives. The latest run flagged a $0.19 entry that ran to $1.10 — +478%, +$2,730 on a single 30-contract position.",
    ticker: "ESPR",
    returnPct: "+478%",
    returnDollar: "+$2,730",
    investmentRequired: "$570",
    thumbnailUrl: "https://storage.googleapis.com/consensus-chat-images/alert-thumbnails/case-study-insider-cluster-small-cap-strategy/20260628T181143-c45d7f57.png",
    tags: ["Insider Buying", "SEC Form 4", "Small Cap", "Strategy Guide"],
    readTime: "7 min",
    holdingPeriod: "2-4 weeks",
    strategy: "Insider Cluster Signal",
    tradeDetail: "Form 4 Code P · Cluster ≥2 insiders · Material vs comp",
    tradeStatus: "exited",
    publishDate: "May 19, 2026",
    views: 11800,
    likes: 742,
    author: {
      name: "ryanMD",
      avatar: "/images/case-studies/insider-cluster/author-ryanmd.png",
      badge: "Xynth Veteran · 3 months",
    },
    prompt: `Scan SEC form 4 filings for open-market stock purchases. Only look for transaction Code P. Then filter for the following:

- Stocks under $500M market cap
- Purchases from a company executive (CEO, CFO, etc) that exceed 5-10% of the person's annual compensation (salary + bonus + stock awards), or purchases that increase the executive's position by 10%+
- 2+ insiders purchasing within 30 days of each other
- Reject any routine purchases (same calendar quarter purchases in prior years)

Then survivability-gate the candidates: Altman Z-score above 1.81, current ratio above 1.0, and reject if more than 30% of debt matures within 12 months on a below-investment-grade rating.

Score the survivors on a 5-component conviction model (purchase quality, cluster strength, contrarian analyst layer, price context, earnings proximity). Take the highest-scoring name and check for the nearest swing low. Suggest an exact trade I can execute with entry, stop, and target.`,
    sections: {
      setup:
        "Multiple company executives significantly increasing their positions within a similar time frame is one of the cleanest signals you can get — but only if you filter the noise. This is the exact 4-step framework I run on every Form 4 filing window: scan, survivability-gate, score, and only then build a trade.",
      whatXynthFound: [
        "Step 1 — Scanned SEC Form 4 Code P open-market purchases under $500M market cap, requiring 2+ unique insiders within 30 days, purchase size material to comp (5-10%+) or position increase ≥10%, and stripped out routine same-quarter buyers.",
        "Step 2 — Ran a survivability gate on the candidates: Altman Z-score ≥ 1.81, current ratio ≥ 1.0, and rejected anything with >30% debt maturing in 12 months on below-investment-grade credit.",
        "Step 3 — Scored every survivor with a 5-component conviction model (purchase quality, cluster strength, contrarian analyst layer, price context, earnings proximity). SDHC came back at 90/100 — the highest-conviction signal in the screen, with 4 unique insiders all buying within 7 days of each other.",
        "Step 4 — On the top name, found the nearest swing low and built an exact trade plan: entry, GTC stop-loss, scaled target around the 30-day high, and a trailed runner. The realized leg shown above was a $0.19 cost-basis call position that ran to $1.10 — +478% / +$2,730.",
      ],
      trade:
        "Common stock buy on the highest-conviction insider cluster, stop-loss at the nearest swing low, scale half off when price hits the 30-day high, trail the rest at entry to lock in a risk-free remainder. Optional call leg for asymmetric upside on the same setup.",
      result:
        "Insider clusters on small caps are one of the few edges retail still has, because big funds can't build meaningful positions in sub-$500M names. The whole pipeline runs in one Xynth conversation every morning — you only get 1-2 viable candidates every 2-3 weeks, but the hit rate is high when the filters all stack.",
    },
  },
];

const DISPLAY_ORDER = [
  "market-recap-jun29-jul3-2026",
  "how-to-day-trade-with-ai",
  "insider-cluster-small-cap-strategy",
  "czr-merger-100pct",
  "ai-options-trading-guide",
  "earnings-iv-crush-84pct",
  "vol-skew-spreads-250pct",
  "whale-watching-flow-strategy",
];

export const CASE_STUDIES: CaseStudy[] = [
  ...DISPLAY_ORDER.map(slug => _RAW_CASE_STUDIES.find(s => s.slug === slug)!),
  ..._RAW_CASE_STUDIES.filter(s => !DISPLAY_ORDER.includes(s.slug)),
];

export function getCaseStudy(slug: string): CaseStudy | null {
  return CASE_STUDIES.find((cs) => cs.slug === slug) ?? null;
}
